<template>
  <v-container fluid>
    <h2>{{ isElev ? $tt('fieldElevation.title') : $tt('feed.vraTitle') }}</h2>
    <p class="ml-2 mb-2 caption grey--text">
      {{ isElev ? $tt('feed.elevDescription') : $tt('feed.vraDescription') }}
    </p>

    <!-- 生成 (対象は圃場または圃場セット。将来: ISOXML インポートボタンをここに並べる) -->
    <v-layout align-center class="ml-2 mb-2">
      <v-btn color="primary" small @click="openGenerate()">
        <v-icon small left>mdi-grid</v-icon>
        {{ $tt('feed.vraGenerate') }}
      </v-btn>
    </v-layout>

    <!-- 圃場一覧と同じ様式: 左=リスト (最小幅)、右=マップ (最大化) -->
    <v-row dense>
      <!-- 保存済みゾーンマップ -->
      <v-col cols="12" md="5" lg="4" class="list-pane">
        <v-simple-table v-if="listRows.length" dense class="list-scroll">
          <tbody>
            <template v-for="(r, ri) in pagedRows">
              <!-- グループ行: クリックで全圃場+全 VRA マップをマップ表示。ツリー展開は > ボタンのみ -->
              <tr v-if="r.type === 'group'" :key="'g-' + r.g.id" class="clickable-row" @click="showGroup(r.g)">
                <td class="py-2">
                  <div class="caption grey--text">{{ r.g.date_from }} - {{ r.g.date_to }}</div>
                  <v-btn small icon class="mr-1" @click.stop="toggleGroup(r.g)">
                    <v-icon small>{{ expandedGroupId === r.g.id ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                  </v-btn>
                  <strong class="d-inline-block text-truncate" style="max-width: 150px">{{ r.g.name }}</strong>
                  <v-chip x-small outlined color="primary" class="ml-1">{{ $tt('feed.mapSets') }}</v-chip>
                  <div class="caption">{{ r.g.index }} / {{ $tt('feed.mapSetFields') }}: {{ r.g.num_maps }}</div>
                </td>
                <td class="text-right pr-1" style="width: 40px" @click.stop>
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small icon v-bind="attrs" v-on="on">
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="openGroupEdit(r.g)">
                        <v-list-item-icon class="mr-2"><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $tt('feed.vraEditMap') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="groupDeleteTarget = r.g">
                        <v-list-item-icon class="mr-2"
                          ><v-icon small color="error">mdi-trash-can-outline</v-icon></v-list-item-icon
                        >
                        <v-list-item-title class="error--text">{{ $tt('customDelete') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
              <!-- 個別マップ行 (グループ展開時のメンバーは字下げ) -->
              <tr v-else :key="'m-' + r.m.id + '-' + ri" class="clickable-row" @click="show(r.m)">
                <td class="py-2" :class="{ 'pl-8': r.nested }">
                  <div class="caption grey--text">
                    {{ r.m.obs_date || r.m.date_from + ' - ' + r.m.date_to }}
                  </div>
                  <strong class="d-inline-block text-truncate" style="max-width: 150px">{{
                    r.m.group_id ? r.m.field_name || r.m.name : r.m.name || r.m.field_name
                  }}</strong>
                  <v-chip v-if="r.m.field_set_id && !r.nested" x-small outlined color="primary" class="ml-1">
                    {{ $tt('feed.mapSets') }}
                  </v-chip>
                  <div class="caption">{{ r.m.index }}</div>
                </td>
                <td class="text-right pr-1" style="width: 40px" @click.stop>
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn small icon v-bind="attrs" v-on="on">
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <!-- セット由来のマップの編集はセット側でのみ行う -->
                      <v-list-item v-if="!r.m.group_id" @click="openEdit(r.m)">
                        <v-list-item-icon class="mr-2"><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $tt('feed.vraEditMap') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="openExport(r.m)">
                        <v-list-item-icon class="mr-2"><v-icon small>mdi-export</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $tt('feed.vraExport') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="deleteTarget = r.m">
                        <v-list-item-icon class="mr-2"
                          ><v-icon small color="error">mdi-trash-can-outline</v-icon></v-list-item-icon
                        >
                        <v-list-item-title class="error--text">{{ $tt('customDelete') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </template>
          </tbody>
        </v-simple-table>
        <list-pager-component
          v-if="listRows.length"
          :total="listRows.length"
          :page.sync="mapsPage"
          :page-size.sync="mapsPageSize"
        />
        <p v-if="!listRows.length" class="caption grey--text">{{ $tt('feed.noManualRecords') }}</p>
      </v-col>

      <v-col cols="12" md="7" lg="8" style="position: relative">
        <!-- ゾーン読み込み・描画中インジケーター -->
        <div v-if="mapLoading" class="map-loading-overlay">
          <v-progress-circular indeterminate color="primary" size="48" width="4" />
        </div>
        <gmap-map
          ref="googleMapRef"
          :center="getDefaultMapCenter()"
          :zoom="getDefaultZoomLevel()"
          map-type-id="roadmap"
          :options="{ streetViewControl: false, rotateControl: false, fullscreenControl: false }"
          style="height: 75vh"
        >
          <!-- ゾーンポリゴン (メッシュ化はエクスポート/アプリ同期時に実施) -->
          <gmap-polygon
            v-for="(z, i) in zonePolys"
            :key="'z-' + i"
            :paths="z.paths"
            :options="{
              fillColor: zoneColor(z.zone),
              fillOpacity: 0.55,
              strokeColor: isElev && contourLines ? '#333333' : '#ffffff',
              strokeWeight: isElev && contourLines ? 1.2 : 0.5,
              clickable: false,
              zIndex: 2,
            }"
          />
          <!-- 圃場の外形線 -->
          <gmap-polygon
            v-if="fieldOutline"
            :paths="fieldOutline"
            :options="{
              fillOpacity: 0,
              strokeColor: '#FF9800',
              strokeWeight: 2,
              clickable: false,
              zIndex: 3,
            }"
          />
          <!-- グループ表示時: 全メンバー圃場の外形線 -->
          <gmap-polygon
            v-for="(o, oi) in groupOutlines"
            :key="'o-' + oi"
            :paths="o"
            :options="{
              fillOpacity: 0,
              strokeColor: '#FF9800',
              strokeWeight: 2,
              clickable: false,
              zIndex: 3,
            }"
          />
        </gmap-map>
        <div v-if="isElev" class="caption mt-1 d-flex align-center">
          <span v-if="result" class="d-flex align-center">
            <span class="mr-1">{{ result.stats.min }}m</span>
            <span class="elev-gradient mr-1" />
            <span>{{ result.stats.max }}m</span>
          </span>
          <v-spacer />
          <v-checkbox v-model="contourLines" dense hide-details class="mt-0 pt-0" :label="$tt('feed.elevContour')" />
        </div>
        <div v-else class="caption mt-1 d-flex align-center">
          <span v-if="result">
            <v-chip x-small class="mr-1" color="#f7fcb9">1</v-chip>&lt; {{ result.thresholds.p30 }}
            <v-chip x-small class="mx-1" color="#addd8e">2</v-chip>{{ result.thresholds.p30 }} -
            {{ result.thresholds.p70 }} <v-chip x-small class="mx-1" color="#31a354" text-color="white">3</v-chip>&gt;
            {{ result.thresholds.p70 }}
            <span class="grey--text ml-2">
              <template v-if="result.obs_date"
                >{{ $tt('feed.vraObsDate') }}: {{ result.obs_date }}</template
              >
              <template v-else
                >{{ $tt('feed.vraImages') }}: {{ result.n_images }}</template
              >
              / avg {{ result.stats.avg }} ({{ result.stats.min }} - {{ result.stats.max }})
            </span>
          </span>
        </div>
      </v-col>
    </v-row>

    <!-- ゾーンマップ生成ダイアログ -->
    <v-dialog v-model="genDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.vraGenerate') }}</v-card-title>
        <v-card-text>
          <!-- 対象: 圃場 (単体) または圃場セット (バッチ) -->
          <v-radio-group v-model="targetType" row dense hide-details class="mt-0 mb-2">
            <v-radio :label="$tt('customFieldHeader')" value="field" />
            <v-radio :label="$tt('feed.mapSets')" value="set" />
          </v-radio-group>
          <v-select
            v-if="targetType === 'field'"
            v-model="fieldPk"
            :items="fieldItems"
            dense
            :label="$tt('customFieldHeader')"
          />
          <v-select
            v-else
            v-model="fieldSetId"
            :items="fieldSetItems"
            dense
            :label="$tt('feed.mapSets')"
            :hint="selectedFieldSet ? $tt('feed.mapSetFields') + ': ' + selectedFieldSet.num_fields : ''"
            persistent-hint
          />
          <v-text-field
            v-model="genName"
            dense
            clearable
            :label="$tt('feed.workName')"
            :placeholder="$tt('feed.vraNameAuto')"
          />
          <v-select v-if="!isElev" v-model="index" :items="['NDVI', 'GNDVI']" dense :label="$tt('feed.vraIndex')" />
          <v-select v-model.number="genBands" :items="bandItems" dense :label="$tt('feed.zoneBands')" />
          <v-select v-model="genPalette" :items="paletteItems" dense :label="$tt('feed.zonePalette')" />
          <!-- 高低差: 生成ソースと色分けレンジ (田んぼ等の微小高低差はレンジ指定が重要) -->
          <template v-if="isElev">
            <v-select v-model="elevSource" :items="elevSourceItems" dense :label="$tt('feed.elevSource')" />
            <!-- GNSS (作業履歴) 算出オプション: 旧・高低差マップと同じフィルター群 -->
            <template v-if="elevSource === 'GNSS'">
              <v-row dense>
                <v-col cols="6">
                  <v-select
                    v-model.number="gnssMeshSize"
                    :items="[{ text: '3m', value: 3 }, { text: '5m', value: 5 }, { text: '10m', value: 10 }]"
                    dense
                    hide-details
                    :label="$tt('fieldElevation.meshSize')"
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="gnssStatusFilter"
                    :items="gnssStatusItems"
                    dense
                    hide-details
                    :label="$tt('fieldElevation.gnssStatus')"
                  />
                </v-col>
              </v-row>
              <v-slider
                v-model="gnssTaskFetchLimit"
                :min="1"
                :max="30"
                class="mt-4"
                :label="$tt('fieldElevation.taskFetchLimit')"
                :hint="$tt('fieldElevation.taskFetchLimitHint')"
                persistent-hint
                thumb-label
              />
              <v-checkbox v-model="gnssUseZRange" dense hide-details :label="$tt('fieldElevation.elevationRange')" />
              <v-row v-if="gnssUseZRange" dense>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="gnssZMin"
                    type="number"
                    step="0.01"
                    suffix="m"
                    dense
                    hide-details
                    :label="$tt('fieldElevation.minValue')"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="gnssZMax"
                    type="number"
                    step="0.01"
                    suffix="m"
                    dense
                    hide-details
                    :label="$tt('fieldElevation.maxValue')"
                  />
                </v-col>
              </v-row>
              <v-checkbox v-model="gnssUseDateRange" dense hide-details :label="$tt('fieldElevation.dateRangeInput')" />
              <v-row v-if="gnssUseDateRange" dense>
                <v-col cols="6">
                  <v-text-field
                    v-model="gnssDateFrom"
                    type="date"
                    dense
                    hide-details
                    :label="$tt('fieldElevation.oldestTaskDateInput')"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="gnssDateTo"
                    type="date"
                    dense
                    hide-details
                    :label="$tt('fieldElevation.newestTaskDateInput')"
                  />
                </v-col>
              </v-row>
            </template>
            <v-row dense>
              <v-col cols="6">
                <v-select
                  v-model="elevCenterMode"
                  :items="elevCenterItems"
                  dense
                  hide-details
                  :label="$tt('feed.elevCenter')"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-if="elevCenterMode === 'custom'"
                  v-model.number="elevCenterValue"
                  type="number"
                  step="0.01"
                  suffix="m"
                  dense
                  hide-details
                  :label="$tt('feed.elevCenterValue')"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="elevRangeMode"
                  :items="elevRangeItems"
                  dense
                  hide-details
                  :label="$tt('feed.elevRange')"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-if="elevRangeMode === 'custom'"
                  v-model.number="elevRangeWidth"
                  type="number"
                  step="0.01"
                  suffix="m"
                  dense
                  hide-details
                  :label="$tt('feed.elevRangeWidth')"
                />
              </v-col>
            </v-row>
          </template>
          <v-row v-if="!isElev" dense>
            <v-col cols="6">
              <v-text-field
                v-model="dateFrom"
                type="date"
                dense
                hide-details
                :label="$tt('customDateFilterStartDate')"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="dateTo" type="date" dense hide-details :label="$tt('customDateFilterEndDate')" />
            </v-col>
          </v-row>
          <!-- 観測日選択は単体生成のみ (バッチは期間モザイク)。高低差は期間非依存 -->
          <v-select
            v-if="targetType === 'field' && !isElev"
            v-model="obsDate"
            :items="obsDateItems"
            :loading="datesLoading"
            dense
            class="mt-3"
            :label="$tt('feed.vraObsDate')"
          />
          <div v-if="!isElev && genBands === 3" class="caption grey--text mb-1">{{ $tt('feed.vraRate') }}</div>
          <v-row v-if="!isElev && genBands === 3" dense>
            <v-col cols="4">
              <v-text-field v-model.number="genRates[1]" type="number" min="0" dense hide-details>
                <template v-slot:prepend-inner
                  ><v-chip x-small color="#f7fcb9">1</v-chip></template
                >
              </v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field v-model.number="genRates[2]" type="number" min="0" dense hide-details>
                <template v-slot:prepend-inner
                  ><v-chip x-small color="#addd8e">2</v-chip></template
                >
              </v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field v-model.number="genRates[3]" type="number" min="0" dense hide-details>
                <template v-slot:prepend-inner
                  ><v-chip x-small color="#31a354" text-color="white">3</v-chip></template
                >
              </v-text-field>
            </v-col>
          </v-row>
          <v-progress-linear
            v-if="batchProgress.total"
            :value="(batchProgress.done / batchProgress.total) * 100"
            height="18"
            rounded
            color="primary"
            class="mt-3"
          >
            <span class="caption white--text">{{ batchProgress.done }}/{{ batchProgress.total }}</span>
          </v-progress-linear>
          <p v-if="generating" class="caption primary--text mb-0 mt-2">{{ $tt('feed.vraGenerating') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="generating" @click="genDialog = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn
            color="primary"
            :loading="generating"
            :disabled="targetType === 'field' ? !selectedField : !fieldSetId"
            @click="targetType === 'field' ? generate() : generateForSet()"
          >
            {{ $tt('feed.vraGenerate') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 編集 (名称・ゾーン別施肥量) -->
    <v-dialog v-model="editDialog" max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.vraEditMap') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="editName" dense :label="$tt('feed.workName')" />
          <div v-if="!isEditingElev" class="caption grey--text mb-1">{{ $tt('feed.vraRate') }}</div>
          <v-row v-if="!isEditingElev" dense>
            <v-col v-for="z in editZoneLevels" :key="'er-' + z" cols="4">
              <v-text-field v-model.number="editRates[z]" type="number" min="0" dense hide-details>
                <template v-slot:prepend-inner>
                  <v-chip x-small :color="editZoneColor(z)">{{ z }}</v-chip>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- 段数・色分けの再分類 (再生成せずに変更できる。粒度は生成時のゾーン片が上限) -->
          <v-divider class="my-3" />
          <div class="caption grey--text mb-1">{{ $tt('feed.reclassifyTitle') }}</div>
          <v-row dense>
            <v-col cols="6">
              <v-select
                v-model.number="editBands"
                :items="bandItems"
                dense
                hide-details
                :label="$tt('feed.zoneBands')"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="editPalette"
                :items="paletteItems"
                dense
                hide-details
                :label="$tt('feed.zonePalette')"
              />
            </v-col>
          </v-row>
          <v-row v-if="isEditingElev" dense>
            <v-col cols="6">
              <v-select
                v-model="elevCenterMode"
                :items="elevCenterItems"
                dense
                hide-details
                :label="$tt('feed.elevCenter')"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-if="elevCenterMode === 'custom'"
                v-model.number="elevCenterValue"
                type="number"
                step="0.01"
                suffix="m"
                dense
                hide-details
                :label="$tt('feed.elevCenterValue')"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="elevRangeMode"
                :items="elevRangeItems"
                dense
                hide-details
                :label="$tt('feed.elevRange')"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-if="elevRangeMode === 'custom'"
                v-model.number="elevRangeWidth"
                type="number"
                step="0.01"
                suffix="m"
                dense
                hide-details
                :label="$tt('feed.elevRangeWidth')"
              />
            </v-col>
          </v-row>
          <v-btn small outlined color="primary" class="mt-2" :loading="reclassifying" @click="applyReclassify()">
            {{ $tt('feed.reclassifyApply') }}
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="editSaving" @click="editDialog = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="editSaving" @click="saveEdit()">{{ $tt('customSaveAction') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ISOXML エクスポート (ISO 11783-10: メッシュ=Grid type 1 / ポリゴン=TZN)。施肥量は保存済みの値を使用 -->
    <v-dialog v-model="exportDialog" max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.vraExport') }}</v-card-title>
        <v-card-text>
          <v-radio-group v-model="exportMode" row dense hide-details class="mt-0 mb-3">
            <v-radio :label="$tt('feed.vraModeGrid')" value="grid" />
            <v-radio :label="$tt('feed.vraModePolygon')" value="polygon" />
          </v-radio-group>
          <v-text-field
            v-if="exportMode === 'grid'"
            v-model.number="exportCell"
            type="number"
            min="1"
            max="100"
            dense
            class="mt-3"
            hide-details
            :label="$tt('feed.vraCellSize')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="exporting" @click="exportDialog = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="exporting" @click="doExport()">{{ $tt('feed.vraExport') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :value="!!deleteTarget" max-width="360" @input="deleteTarget = null">
      <v-card>
        <v-card-text class="pt-4">{{ $tt('feed.derivedDeleteConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="error" text @click="deleteMap()">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- グループ (マップセット) の編集: 名称・段数・色分けをメンバー全体に適用 -->
    <v-dialog :value="!!groupEditTarget" max-width="420" @input="groupEditTarget = null">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.vraEditMap') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="groupEditName" dense :label="$tt('feed.workName')" />
          <div v-if="!isGroupEditElev" class="caption grey--text mb-1">{{ $tt('feed.vraRate') }}</div>
          <v-row v-if="!isGroupEditElev" dense>
            <v-col v-for="z in groupEditBands" :key="'gr-' + z" cols="4">
              <v-text-field v-model.number="groupEditRates[z]" type="number" min="0" dense hide-details>
                <template v-slot:prepend-inner>
                  <v-chip x-small :color="groupEditZoneColor(z)">{{ z }}</v-chip>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-3" />
          <div class="caption grey--text mb-1">{{ $tt('feed.reclassifyTitle') }}</div>
          <v-row dense>
            <v-col cols="6">
              <v-select
                v-model.number="groupEditBands"
                :items="bandItems"
                dense
                hide-details
                :label="$tt('feed.zoneBands')"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="groupEditPalette"
                :items="paletteItems"
                dense
                hide-details
                :label="$tt('feed.zonePalette')"
              />
            </v-col>
          </v-row>
          <v-row v-if="isGroupEditElev" dense>
            <v-col cols="6">
              <v-select
                v-model="elevCenterMode"
                :items="elevCenterItems"
                dense
                hide-details
                :label="$tt('feed.elevCenter')"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-if="elevCenterMode === 'custom'"
                v-model.number="elevCenterValue"
                type="number"
                step="0.01"
                suffix="m"
                dense
                hide-details
                :label="$tt('feed.elevCenterValue')"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="elevRangeMode"
                :items="elevRangeItems"
                dense
                hide-details
                :label="$tt('feed.elevRange')"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-if="elevRangeMode === 'custom'"
                v-model.number="elevRangeWidth"
                type="number"
                step="0.01"
                suffix="m"
                dense
                hide-details
                :label="$tt('feed.elevRangeWidth')"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="groupSaving" @click="groupEditTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="groupSaving" :disabled="!groupEditName" @click="saveGroupEdit()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- グループ削除 (メンバーマップも一括削除) -->
    <v-dialog :value="!!groupDeleteTarget" max-width="380" @input="groupDeleteTarget = null">
      <v-card>
        <v-card-text class="pt-4">{{ $tt('feed.mapSetDeleteConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="groupDeleteTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="error" text @click="deleteGroup()">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import GoogleMapMixin from '@/components/mixins/google-map-mixin'
import feedApiClient from '@/lib/feed-api-client'
import { evaluateFieldSetFilter, resolveFieldPolygons } from '@/lib/field-set-filter'
import Toast from '@/lib/toast'
import ListPagerComponent from '@/components/ListPagerComponent'
import { emitFeedEvent, polygonCenter, zonesShape } from '@/lib/feed-events'
import { paletteColor, normPalette, DEFAULT_PALETTE } from '@/lib/zone-palette'
import * as Constants from '@/lib/constants'
import UserInfo from '@/lib/user-info'
import connectDatastoreClient from '@/lib/connect-client/connect-datastore-client'
import connectNaviClient from '@/lib/connect-client/connect-navi-client'

export default {
  name: 'VraMap',
  mixins: [GoogleMapMixin],
  components: { ListPagerComponent },
  props: {
    // 'vra' = VRAマップ / 'elev' = 高低差マップ (同じ zone_maps 構造で指数 ELEV)
    mode: { type: String, default: 'vra' },
  },
  data() {
    const now = Date.now()
    return {
      genDialog: false,
      targetType: 'field',
      fieldPk: null,
      fieldSetId: null,
      fieldSets: [],
      batchProgress: { done: 0, total: 0 },
      index: 'NDVI',
      dateFrom: new Date(now - 90 * 86400000).toISOString().slice(0, 10),
      dateTo: new Date(now).toISOString().slice(0, 10),
      obsDate: null,
      dates: [],
      datesLoading: false,
      datesSeq: 0,
      generating: false,
      result: null,
      zonePolys: [],
      groupOutlines: [],
      maps: [],
      groups: [],
      expandedGroupId: null,
      mapLoading: false,
      groupEditTarget: null,
      groupEditName: '',
      groupEditBands: 3,
      groupEditPalette: DEFAULT_PALETTE,
      groupEditRates: { 1: 50, 2: 100, 3: 150 },
      groupSaving: false,
      groupDeleteTarget: null,
      mapsPage: 1,
      mapsPageSize: 20,
      // 高低差 (ELEV) の生成ソースと色分けレンジ設定
      elevSource: 'GNSS',
      elevCenterMode: 'median',
      elevCenterValue: null,
      elevRangeMode: 'minmax',
      elevRangeWidth: null,
      contourLines: false,
      genBands: 3,
      genName: '',
      editBands: 3,
      editZoneLevels: [1, 2, 3],
      reclassifying: false,
      currentMaxZone: 3,
      // GNSS (作業履歴) 算出オプション (旧・高低差マップのフィルター群)
      gnssMeshSize: Constants.FieldElevationMap.DEFAULT_MESH_SIZE,
      gnssTaskFetchLimit: Constants.FieldElevationMap.DEFAULT_TASK_FETCH_LIMIT,
      gnssStatusFilter: Constants.FieldElevationMap.DEFAULT_GNSS_STATUS_FILTER_VALUE,
      gnssUseZRange: false,
      gnssZMin: null,
      gnssZMax: null,
      gnssUseDateRange: false,
      gnssDateFrom: '',
      gnssDateTo: '',
      deleteTarget: null,
      exportDialog: false,
      exportTarget: null,
      exportMode: 'grid',
      exportCell: 10,
      exporting: false,
      genRates: { 1: 50, 2: 100, 3: 150 },
      genPalette: DEFAULT_PALETTE,
      editPalette: DEFAULT_PALETTE,
      currentPalette: DEFAULT_PALETTE,
      editDialog: false,
      editTarget: null,
      editName: '',
      editRates: { 1: 50, 2: 100, 3: 150 },
      editSaving: false,
    }
  },
  computed: {
    isGroupEditElev() {
      return !!this.groupEditTarget && this.groupEditTarget.index === 'ELEV'
    },
    paletteItems() {
      return [
        { value: 'hue', text: this.$tt('feed.paletteHue') },
        { value: 'ylgn', text: this.$tt('feed.paletteYlgn') },
        { value: 'gnrd', text: this.$tt('feed.paletteGnrd') },
      ]
    },
    gnssStatusItems() {
      return Constants.FieldElevationMap.GNSS_STATUS_FILTERS
    },
    bandItems() {
      // ISOXML 準拠: 2..251 段 (0-250 の 251 レベル)
      const items = []
      for (let i = 2; i <= 251; i++) {
        items.push(i)
      }
      return items
    },
    elevSourceItems() {
      return [
        { text: this.$tt('feed.elevSourceDem'), value: 'DEM' },
        { text: this.$tt('feed.elevSourceGnss'), value: 'GNSS' },
      ]
    },
    elevCenterItems() {
      return [
        { text: this.$tt('feed.elevCenterMedian'), value: 'median' },
        { text: this.$tt('feed.elevCenterMean'), value: 'mean' },
        { text: this.$tt('feed.elevCustom'), value: 'custom' },
      ]
    },
    elevRangeItems() {
      return [
        { text: 'MIN-MAX', value: 'minmax' },
        { text: '±2σ', value: 'stddev' },
        { text: this.$tt('feed.elevCustom'), value: 'custom' },
      ]
    },
    isElev() {
      return this.mode === 'elev'
    },
    isEditingElev() {
      return !!(this.editTarget && this.editTarget.index === 'ELEV')
    },
    // このページのモードに合うマップ/グループだけを表示する (ELEV = 高低差)
    modeMaps() {
      return this.maps.filter(m => (m.index === 'ELEV') === this.isElev)
    },
    modeGroups() {
      return this.groups.filter(g => (g.index === 'ELEV') === this.isElev)
    },
    // グループ行 (展開時はメンバーを続けて表示) + グループ未所属の個別マップ行
    listRows() {
      const rows = []
      const groupedIds = new Set(this.modeGroups.map(g => g.id))
      for (const g of this.modeGroups) {
        rows.push({ type: 'group', g })
        if (this.expandedGroupId === g.id) {
          for (const m of this.modeMaps.filter(m => m.group_id === g.id)) {
            rows.push({ type: 'map', m, nested: true })
          }
        }
      }
      for (const m of this.modeMaps.filter(m => !m.group_id || !groupedIds.has(m.group_id))) {
        rows.push({ type: 'map', m })
      }
      return rows
    },
    pagedRows() {
      const start = (this.mapsPage - 1) * this.mapsPageSize
      return this.listRows.slice(start, start + this.mapsPageSize)
    },
    fieldItems() {
      return this.$store.getters.fields.map(f => ({ text: f.name, value: f.pk }))
    },
    fieldSetItems() {
      return this.fieldSets.map(s => ({ text: s.name, value: s.id }))
    },
    selectedFieldSet() {
      return this.fieldSets.find(s => s.id === this.fieldSetId) || null
    },
    selectedField() {
      return this.$store.getters.fields.find(f => f.pk === this.fieldPk) || null
    },
    fieldOutline() {
      const f = this.selectedField
      const poly = f && this.fieldPolygon(f)
      if (!poly) return null
      return poly.coordinates[0].map(c => ({ lat: c[1], lng: c[0] }))
    },
    obsDateItems() {
      const items = [{ text: this.$tt('feed.vraPeriodMean'), value: null }]
      for (const d of this.dates) {
        items.push({ text: d, value: d })
      }
      return items
    },
  },
  watch: {
    mode() {
      this.index = this.isElev ? 'ELEV' : 'NDVI'
      this.genBands = this.isElev ? 251 : 3
      this.mapsPage = 1
      this.expandedGroupId = null
      this.result = null
      this.zonePolys = []
      this.groupOutlines = []
      this.fieldPk = null
    },
    fieldPk() {
      this.fitToField()
      this.loadDates()
    },
    dateFrom() {
      this.loadDates()
    },
    dateTo() {
      this.loadDates()
    },
  },
  mounted() {
    if (this.isElev) {
      this.index = 'ELEV'
      this.genBands = 251
    }
    this.loadMaps()
    this.loadGroups()
    this.fitToFields()
  },
  methods: {
    zoneColor(z) {
      const max = Math.max(this.currentMaxZone, 2)
      return paletteColor(this.currentPalette, (z - 1) / Math.max(max - 1, 1))
    },
    editZoneColor(z) {
      const max = Math.max(this.editZoneLevels.length, 2)
      return paletteColor(this.editPalette, (z - 1) / Math.max(max - 1, 1))
    },
    // グループのメンバーマップからゾーン色分け形状を作る (先頭4圃場・サイズ超過時は外形)
    async groupZonesShape(groupId, fallback) {
      if (!groupId) return fallback
      try {
        const res = await feedApiClient.zoneMaps()
        const members = (res.data.records || []).filter(m => m.group_id === groupId).slice(0, 4)
        const zones = []
        for (const m of members) {
          const d = await feedApiClient.zoneMapDetail(m.id)
          for (const z of d.data.zones || []) {
            zones.push(z)
          }
        }
        return zonesShape(zones, fallback, normPalette((members[0] || {}).palette))
      } catch (e) {
        console.warn(e)
        return fallback
      }
    },
    // メンバー圃場群を MultiPolygon にまとめる (フィード表示用)
    multiPolygonOf(fields) {
      const coords = (fields || []).map(f => f.polygon && f.polygon.coordinates).filter(Boolean)
      return coords.length ? { type: 'MultiPolygon', coordinates: coords } : undefined
    },
    // 生成/取り込みに渡す色分けレンジ設定
    elevParams() {
      const center = this.elevCenterMode === 'custom' ? Number(this.elevCenterValue) : this.elevCenterMode
      const range = this.elevRangeMode === 'custom' ? Number(this.elevRangeWidth) : this.elevRangeMode
      return { elev_center: center, elev_range: range }
    },
    openGenerate() {
      this.genName = ''
      this.genDialog = true
      if (this.fieldPk && !this.dates.length) {
        this.loadDates()
      }
      this.loadFieldSets()
    },
    // datastore の GNSS 高低差マップからメッシュセル (geometry + 標高 z) を取り出す。
    // ダイアログで指定したフィルター (GNSS状態・参照データ量・メッシュ・標高/日付範囲) で
    // 常に再計算してから取り込む
    async fetchGnssCells(field) {
      const client = UserInfo.isFSMigrated() ? connectDatastoreClient : connectNaviClient
      const params = {
        field: { id: field.id, pk: field.pk },
        meshSize: this.gnssMeshSize,
        taskFetchLimit: this.gnssTaskFetchLimit,
        gnssStatusFilter: this.gnssStatusFilter,
      }
      if (this.gnssUseZRange && this.gnssZMin != null && this.gnssZMax != null) {
        params.zRange = { min: this.gnssZMin, max: this.gnssZMax }
      }
      if (this.gnssUseDateRange && this.gnssDateFrom && this.gnssDateTo) {
        params.dateRange = { oldest: this.gnssDateFrom, newest: this.gnssDateTo }
      }
      await client.createFieldElevationMap(params)
      let map = null
      for (let i = 0; i < 36; i++) {
        await new Promise(r => setTimeout(r, 5000))
        try {
          const res = await client.getFieldElevationMap({ id: field.id, pk: field.pk })
          map = res.data
        } catch (e) {
          // 作成直後は 404 があり得る
        }
        if (map && map.status !== 'Creating') break
      }
      if (!map || map.status !== 'Completed') {
        throw new Error('gnss elevation map not ready')
      }
      let gj = map.geoJson
      if (typeof gj === 'string') gj = JSON.parse(gj)
      return (gj.features || [])
        .filter(f => (f.properties || {})['aid-type'] === 'field-elevation-mesh')
        .map(f => ({ geometry: f.geometry, z: Number((f.properties || {}).z) }))
    },
    // GNSS 由来の高低差をセット単位で取り込む (メンバーごとに取得→一括インポート)
    async importGnssGroup(s) {
      const attrsRes = await feedApiClient.fieldAttrs()
      const attrs = attrsRes.data.attrs || {}
      const members = evaluateFieldSetFilter(this.$store.getters.fields, attrs, s.filter)
      const resolved = resolveFieldPolygons(members)
      this.batchProgress = { done: 0, total: resolved.length }
      const maps = []
      const failed = []
      for (let i = 0; i < resolved.length; i++) {
        const rf = resolved[i]
        const storeField = members.find(f => String(f.pk) === String(rf.field_id))
        try {
          const cells = await this.fetchGnssCells(storeField)
          maps.push({
            field_id: rf.field_id,
            field_name: rf.field_name,
            polygon: rf.polygon,
            cells,
            bands: this.genBands,
            ...this.elevParams(),
          })
        } catch (e) {
          console.error(e)
          failed.push({ field_name: rf.field_name })
        }
        this.batchProgress = { done: i + 1, total: resolved.length }
      }
      if (!maps.length) {
        Toast.i18nError('feed.elevGnssFailed')
        return
      }
      const res = await feedApiClient.importElevationGroup({
        field_set_id: s.id,
        maps,
        palette: this.genPalette,
        name: this.genName || undefined,
      })
      if (res.data.created > 0) {
        const shape = await this.groupZonesShape(res.data.group_id, this.multiPolygonOf(resolved))
        emitFeedEvent('elevation_map_added', this.genName || s.name + ' ELEV', undefined, shape)
      }
      const failedTotal = failed.length + (res.data.failed || []).length
      if (failedTotal) {
        Toast.error(this.$tt('feed.mapSetGeneratedWithCount', { created: res.data.created, failed: failedTotal }))
      } else {
        Toast.success(this.$tt('feed.mapSetGeneratedWithCount', { created: res.data.created, failed: 0 }))
      }
    },
    async loadFieldSets() {
      try {
        const res = await feedApiClient.fieldSets()
        this.fieldSets = res.data.records || []
      } catch (e) {
        console.error(e.response || e)
      }
    },
    fieldPolygon(f) {
      try {
        const g = JSON.parse(f.geoJson)
        const feature = g.features && g.features[0]
        if (feature && feature.geometry && feature.geometry.type === 'Polygon') {
          return feature.geometry
        }
      } catch (e) {
        console.error(e)
      }
      return null
    },
    fitBoundsOf(pointsList) {
      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.setTilt(0)
        const bounds = new google.maps.LatLngBounds()
        let has = false
        for (const pts of pointsList) {
          for (const p of pts) {
            has = true
            bounds.extend(p)
          }
        }
        if (has) {
          map.fitBounds(bounds)
        }
      })
    },
    fitToFields() {
      const all = []
      for (const f of this.$store.getters.fields) {
        const poly = this.fieldPolygon(f)
        if (poly) {
          all.push(poly.coordinates[0].map(c => ({ lat: c[1], lng: c[0] })))
        }
      }
      this.fitBoundsOf(all)
    },
    fitToField() {
      if (this.fieldOutline) {
        this.fitBoundsOf([this.fieldOutline])
      }
    },
    // 期間内に利用できる観測日を取得 (元 JS の画像検索ステップに相当)
    async loadDates() {
      if (this.isElev) return
      const f = this.selectedField
      const polygon = f && this.fieldPolygon(f)
      this.dates = []
      this.obsDate = null
      if (!polygon || !this.dateFrom || !this.dateTo) return
      const seq = ++this.datesSeq
      this.datesLoading = true
      try {
        const res = await feedApiClient.zoneMapDates({
          polygon,
          date_from: this.dateFrom,
          date_to: this.dateTo,
        })
        if (seq === this.datesSeq) {
          this.dates = res.data.dates || []
        }
      } catch (e) {
        console.error(e.response || e)
      } finally {
        if (seq === this.datesSeq) {
          this.datesLoading = false
        }
      }
    },
    renderZones(zones) {
      this.currentMaxZone = Math.max(3, ...(zones || []).map(z => z.zone || 0))
      this.zonePolys = []
      for (const z of zones || []) {
        const geom = z.geometry || {}
        const polys =
          geom.type === 'MultiPolygon' ? geom.coordinates : geom.type === 'Polygon' ? [geom.coordinates] : []
        for (const rings of polys) {
          this.zonePolys.push({
            paths: rings.map(ring => ring.map(c => ({ lat: c[1], lng: c[0] }))),
            zone: z.zone,
            mean: z.mean,
          })
        }
      }
      this.fitBoundsOf(this.zonePolys.map(p => p.paths[0] || []))
    },
    async generate() {
      const f = this.selectedField
      const polygon = f && this.fieldPolygon(f)
      if (!polygon) return
      this.generating = true
      try {
        if (this.isElev && this.elevSource === 'GNSS') {
          // 作業履歴 GNSS 由来: datastore の高低差メッシュを取り込む
          try {
            const cells = await this.fetchGnssCells(f)
            const res = await feedApiClient.importElevationZoneMap({
              field_id: String(f.pk),
              field_name: f.name,
              polygon,
              cells,
              bands: this.genBands,
              palette: this.genPalette,
              name: this.genName || undefined,
              ...this.elevParams(),
            })
            this.result = { thresholds: res.data.thresholds, stats: res.data.stats, n_images: 1, obs_date: null }
            this.genDialog = false
            this.loadMaps()
            this.loadGroups()
            emitFeedEvent(
              'elevation_map_added',
              this.genName || f.name,
              polygonCenter(polygon),
              zonesShape(res.data.zones, polygon, this.genPalette)
            )
          } catch (e) {
            // 衛星 (雲・期間) とは別の、作業履歴データ由来のメッセージを出す
            console.error(e.response || e)
            Toast.i18nError('feed.elevGnssFailed')
          } finally {
            this.generating = false
          }
          return
        }
        const res = await feedApiClient.createZoneMap({
          polygon,
          name: this.genName || f.name + ' ' + this.index + ' ' + (this.obsDate || this.dateTo || ''),
          field_id: String(f.pk),
          field_name: f.name,
          index: this.index,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          obs_date: this.obsDate,
          rates: this.genRates,
          bands: this.genBands,
          palette: this.genPalette,
          ...this.elevParams(),
        })
        this.result = res.data
        this.genDialog = false
        this.currentPalette = this.genPalette
        this.renderZones(res.data.zones)
        this.loadMaps()
        emitFeedEvent(
          this.isElev ? 'elevation_map_added' : 'vra_map_added',
          this.genName || f.name + ' ' + this.index,
          polygonCenter(polygon),
          zonesShape(res.data.zones, polygon, this.genPalette)
        )
      } catch (e) {
        console.error(e.response || e)
        if (e.response && e.response.status === 429) {
          Toast.i18nError('feed.vraLimitReached')
        } else {
          Toast.i18nError('feed.vraFailed')
        }
      } finally {
        this.generating = false
      }
    },
    // 圃場セットを対象にしたバッチ生成: フィルタ再評価 → メンバー差し替え → タイルを順に実行
    async generateForSet() {
      const s = this.selectedFieldSet
      if (!s) return
      this.generating = true
      this.batchProgress = { done: 0, total: 0 }
      try {
        // 動的フィルタ: 生成のたびに再評価してメンバーを更新する
        let memberShape
        if (s.filter) {
          const attrsRes = await feedApiClient.fieldAttrs()
          const attrs = attrsRes.data.attrs || {}
          const fields = resolveFieldPolygons(evaluateFieldSetFilter(this.$store.getters.fields, attrs, s.filter))
          if (fields.length) {
            await feedApiClient.replaceFieldSetFields(s.id, fields)
            memberShape = this.multiPolygonOf(fields)
          }
        }

        if (this.isElev && this.elevSource === 'GNSS') {
          await this.importGnssGroup(s)
          this.genDialog = false
          this.loadMaps()
          this.loadGroups()
          this.generating = false
          this.batchProgress = { done: 0, total: 0 }
          return
        }

        const start = await feedApiClient.startFieldSetGeneration(s.id, {
          index: this.index,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          rates: this.genRates,
          bands: this.genBands,
          palette: this.genPalette,
          name: this.genName || undefined,
          ...this.elevParams(),
        })
        const jobId = start.data.job_id
        const tiles = start.data.tiles_total
        this.batchProgress = { done: 0, total: tiles }
        let createdCount = 0
        let failedList = []
        for (let i = 0; i < tiles; i++) {
          const tileRes = await feedApiClient.runFieldSetTile(s.id, jobId, i)
          createdCount += tileRes.data.created || 0
          failedList = failedList.concat(tileRes.data.failed || [])
          this.batchProgress = { done: i + 1, total: tiles }
        }
        // 生成/失敗の件数を明示する (置き換え生成のため一覧の件数は増えないことがある)
        if (failedList.length) {
          Toast.error(
            this.$tt('feed.mapSetGeneratedWithCount', { created: createdCount, failed: failedList.length }) +
              ' (' +
              failedList.map(f => f.field_name).join(', ') +
              ')'
          )
        } else {
          Toast.success(this.$tt('feed.mapSetGeneratedWithCount', { created: createdCount, failed: 0 }))
        }
        this.genDialog = false
        this.loadMaps()
        this.loadGroups()
        if (createdCount > 0) {
          const shape = await this.groupZonesShape(start.data.group_id, memberShape)
          emitFeedEvent(
            this.isElev ? 'elevation_map_added' : 'vra_map_added',
            this.genName || s.name + ' ' + this.index,
            undefined,
            shape
          )
        }
      } catch (e) {
        console.error(e.response || e)
        if (e.response && e.response.status === 429) {
          Toast.i18nError('feed.vraLimitReached')
        } else {
          Toast.i18nError('feed.vraFailed')
        }
      } finally {
        this.generating = false
        this.batchProgress = { done: 0, total: 0 }
      }
    },
    async loadMaps() {
      try {
        const res = await feedApiClient.zoneMaps()
        this.maps = res.data.records || []
      } catch (e) {
        console.error(e.response || e)
      }
    },
    async loadGroups() {
      try {
        const res = await feedApiClient.zoneMapGroups()
        this.groups = res.data.records || []
      } catch (e) {
        console.error(e.response || e)
      }
    },
    toggleGroup(g) {
      this.expandedGroupId = this.expandedGroupId === g.id ? null : g.id
    },
    openGroupEdit(g) {
      this.groupEditTarget = g
      this.groupEditName = g.name || ''
      // 先頭メンバーから現在の段数・色系統・施肥量を初期表示する
      const first = this.maps.find(m => m.group_id === g.id)
      const levels = Object.keys((first && first.rates) || {})
        .map(Number)
        .filter(z => z >= 1)
        .sort((a, b) => a - b)
      this.groupEditBands = levels.length || 3
      this.groupEditPalette = normPalette(first && first.palette)
      const rates = {}
      for (let z = 1; z <= this.groupEditBands; z++) {
        const v = first && first.rates ? first.rates[String(z)] : undefined
        rates[z] = Number(v != null ? v : 50 * z)
      }
      this.groupEditRates = rates
    },
    groupEditZoneColor(z) {
      const max = Math.max(this.groupEditBands, 2)
      return paletteColor(this.groupEditPalette, (z - 1) / Math.max(max - 1, 1))
    },
    // グループ編集の保存: 名称はグループへ、段数・色分け・施肥量は全メンバーマップへ適用
    async saveGroupEdit() {
      const g = this.groupEditTarget
      if (!g) return
      this.groupSaving = true
      try {
        await feedApiClient.updateZoneMapGroup(g.id, { name: this.groupEditName })
        const members = this.maps.filter(m => m.group_id === g.id)
        const isElev = g.index === 'ELEV'
        const rates = {}
        for (let z = 1; z <= this.groupEditBands; z++) {
          rates[z] = Number(this.groupEditRates[z] != null ? this.groupEditRates[z] : 50 * z)
        }
        for (const m of members) {
          const body = { bands: this.groupEditBands, palette: this.groupEditPalette }
          if (isElev) {
            Object.assign(body, this.elevParams())
          }
          await feedApiClient.reclassifyZoneMap(m.id, body)
          if (!isElev) {
            await feedApiClient.updateZoneMap(m.id, { rates })
          }
        }
        this.groupEditTarget = null
        await this.loadMaps()
        this.loadGroups()
        // 表示中のグループなら再描画
        if (this.expandedGroupId === g.id || members.some(m => this.result && m.id === this.result.id)) {
          this.showGroup(g)
        }
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.saveFailed')
      } finally {
        this.groupSaving = false
      }
    },
    async deleteGroup() {
      const g = this.groupDeleteTarget
      if (!g) return
      try {
        await feedApiClient.deleteZoneMapGroup(g.id)
        this.groups = this.groups.filter(x => x.id !== g.id)
        this.loadMaps()
      } catch (e) {
        console.error(e.response || e)
      } finally {
        this.groupDeleteTarget = null
      }
    },
    async show(m) {
      this.mapLoading = true
      try {
        const res = await feedApiClient.zoneMapDetail(m.id)
        this.result = {
          thresholds: res.data.thresholds,
          stats: res.data.stats,
          n_images: res.data.n_images,
          obs_date: res.data.obs_date,
        }
        this.groupOutlines = []
        this.currentPalette = normPalette(m.palette)
        // 元圃場が分かる場合は外形線も重ねる
        if (m.field_id) {
          const f = this.$store.getters.fields.find(x => String(x.pk) === String(m.field_id))
          if (f) {
            this.fieldPk = f.pk
          }
        }
        this.renderZones(res.data.zones)
      } catch (e) {
        console.error(e.response || e)
      } finally {
        this.mapLoading = false
      }
    },
    // グループ行クリック: 全メンバー圃場の外形+全 VRA マップのゾーンをまとめて表示する
    async showGroup(g) {
      this.result = null
      this.fieldPk = null
      this.mapLoading = true
      const members = this.maps.filter(m => m.group_id === g.id)
      this.currentPalette = normPalette((members[0] || {}).palette)
      const allZones = []
      const outlines = []
      for (const m of members) {
        try {
          const res = await feedApiClient.zoneMapDetail(m.id)
          for (const z of res.data.zones || []) {
            allZones.push(z)
          }
        } catch (e) {
          console.error(e.response || e)
        }
        if (m.field_id) {
          const f = this.$store.getters.fields.find(x => String(x.pk) === String(m.field_id))
          const poly = f && this.fieldPolygon(f)
          if (poly) {
            outlines.push(poly.coordinates[0].map(c => ({ lat: c[1], lng: c[0] })))
          }
        }
      }
      this.groupOutlines = outlines
      this.renderZones(allZones)
      this.fitBoundsOf(this.zonePolys.map(p => p.paths[0] || []).concat(outlines))
      this.mapLoading = false
    },
    openEdit(m) {
      this.editTarget = m
      this.editName = m.name || ''
      const r = m.rates || {}
      const levels = Object.keys(r)
        .map(Number)
        .filter(z => z >= 1)
        .sort((a, b) => a - b)
      this.editZoneLevels = levels.length ? levels : [1, 2, 3]
      const rates = {}
      for (const z of this.editZoneLevels) {
        rates[z] = Number(r[String(z)] != null ? r[String(z)] : 50 * z)
      }
      this.editRates = rates
      this.editBands = this.editZoneLevels.length
      this.editPalette = normPalette(m.palette)
      this.editDialog = true
    },
    // 段数・色分けの再分類 (保存済みゾーン片の値から再計算)
    async applyReclassify() {
      const m = this.editTarget
      if (!m) return
      this.reclassifying = true
      try {
        const body = { bands: this.editBands, palette: this.editPalette }
        if (this.isEditingElev) {
          Object.assign(body, this.elevParams())
        }
        const res = await feedApiClient.reclassifyZoneMap(m.id, body)
        // rates 入力を新しい段数で作り直す
        const r = res.data.rates || {}
        const levels = Object.keys(r)
          .map(Number)
          .sort((a, b) => a - b)
        this.editZoneLevels = levels.length ? levels : [1, 2, 3]
        const rates = {}
        for (const z of this.editZoneLevels) {
          rates[z] = Number(r[String(z)])
        }
        this.editRates = rates
        await this.loadMaps()
        const updated = this.maps.find(x => x.id === m.id)
        if (updated) {
          this.editTarget = updated
          this.show(updated)
        }
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.vraFailed')
      } finally {
        this.reclassifying = false
      }
    },
    async saveEdit() {
      const m = this.editTarget
      if (!m) return
      this.editSaving = true
      try {
        // ELEV マップの rates はゾーン平均標高のため上書きしない (名称のみ)
        const body = this.isEditingElev
          ? { name: this.editName, palette: this.editPalette }
          : { name: this.editName, rates: this.editRates, palette: this.editPalette }
        await feedApiClient.updateZoneMap(m.id, body)
        this.editDialog = false
        this.loadMaps()
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.vraFailed')
      } finally {
        this.editSaving = false
      }
    },
    openExport(m) {
      this.exportTarget = m
      this.exportDialog = true
    },
    async doExport() {
      const m = this.exportTarget
      if (!m) return
      this.exporting = true
      try {
        const res = await feedApiClient.exportZoneMapIsoxml(m.id, {
          mode: this.exportMode,
          cell: this.exportCell,
        })
        const url = URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = 'TASKDATA_' + (m.name || m.id).replace(/[^\w\-]+/g, '_') + '.zip'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.exportDialog = false
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.vraFailed')
      } finally {
        this.exporting = false
      }
    },
    async deleteMap() {
      const m = this.deleteTarget
      if (!m) return
      try {
        await feedApiClient.deleteZoneMap(m.id)
        this.maps = this.maps.filter(r => r.id !== m.id)
      } catch (e) {
        console.error(e.response || e)
      } finally {
        this.deleteTarget = null
      }
    },
  },
}
</script>

<style scoped>
.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 12px;
  right: 12px;
  height: 75vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.35);
  z-index: 5;
}

.elev-gradient {
  display: inline-block;
  width: 160px;
  height: 10px;
  border-radius: 5px;
  background: linear-gradient(to right, #1a9850, #a6d96a, #fee08b, #f46d43, #d73027);
}
.clickable-row {
  cursor: pointer;
}

/* リスト列: マップと同じ高さで、テーブルはスクロール・ページャーは最下段に固定 */
.list-pane {
  display: flex;
  flex-direction: column;
  height: 75vh;
}
.list-pane .list-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
}
</style>
